import { z } from "zod";

/** Location Schemas **/
export const PostLocationSchema = z.object({
  names: z.string().min(2),
  latitude: z.number(),
  longitude: z.number(),
  address: z.string(),
  notes: z.string(),
  town: z.object({ id: z.string().uuid(), names: z.string() }),
});

export type PostLocationValues = z.infer<typeof PostLocationSchema>;

export const PatchLocationSchema = z.object({
  names: z.string().min(2).optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  address: z.string().optional(),
  notes: z.string().optional(),
  town: z.object({ id: z.string().uuid(), names: z.string() }).optional(),
});

export type PatchLocationValues = z.infer<typeof PatchLocationSchema>;

export const PostCompositeLocationSchema = z.object({
  names: z.string().min(2),
  notes: z.string(),
  sections: z.array(
    z.object({
      dateRange: z.object({
        startDate: z.string().min(1, "Start date is required"),
        endDate: z.string().min(1, "End date is required"),
      }),
      locations: z.array(z.object({ id: z.string().uuid(), names: z.string() })).min(1, "Pick at least one location"),
    }),
  ),
});

export type PostCompositeLocationValues = z.infer<typeof PostCompositeLocationSchema>;

export const PatchCompositeLocationSchema = z.object({
  names: z.string().min(2).optional(),
  notes: z.string().optional(),
  sections: z
    .array(
      z.object({
        dateRange: z.object({
          startDate: z.string().min(1, "Start date is required"),
          endDate: z.string().min(1, "End date is required"),
        }),
        locations: z.array(z.object({ id: z.string().uuid(), names: z.string() })).min(1, "Pick at least one location"),
      }),
    )
    .optional(),
});

export type PatchCompositeLocationValues = z.infer<typeof PatchCompositeLocationSchema>;

/** Region Schemas **/
export const PostRegionSchema = z.object({
  names: z.string().min(2),
  goodToKnow: z.string(),
  notes: z.string(),
  country: z.object({ id: z.string().uuid(), names: z.string() }),
});

export type PostRegionValues = z.infer<typeof PostRegionSchema>;

export const PatchRegionSchema = z.object({
  names: z.string().min(2).optional(),
  goodToKnow: z.string().optional(),
  notes: z.string().optional(),
  towns: z.array(z.object({ id: z.string().uuid(), names: z.string() })).optional(),
  country: z.object({ id: z.string().uuid(), names: z.string() }).optional(),
});

export type PatchRegionValues = z.infer<typeof PatchRegionSchema>;

/** Town Schemas **/
export const PostTownSchema = z.object({
  names: z.string().min(2),
  notes: z.string(),
  country: z.object({ id: z.string().uuid(), names: z.string() }),
});

export type PostTownValues = z.infer<typeof PostTownSchema>;

export const PatchTownSchema = z.object({
  names: z.string().min(2).optional(),
  notes: z.string().optional(),
  regions: z.array(z.object({ id: z.string().uuid(), names: z.string() })).optional(),
  country: z.object({ id: z.string().uuid(), names: z.string() }).optional(),
});

export type PatchTownValues = z.infer<typeof PatchTownSchema>;

/** Country Schemas **/
export const PostCountrySchema = z.object({
  names: z.string().min(2),
  code: z.string().min(1),
  tlr: z.object({ id: z.string().uuid(), names: z.string() }).nullable().optional(),
});

export type PostCountryValues = z.infer<typeof PostCountrySchema>;

export const PatchCountrySchema = z.object({
  names: z.string().min(2).optional(),
  code: z.string().min(1).optional(),
  tlr: z.object({ id: z.string().uuid(), names: z.string() }).nullable().optional(),
});

export type PatchCountryValues = z.infer<typeof PatchCountrySchema>;

/** Top-Level Region Schemas **/
export const PostTopLevelRegionSchema = z.object({
  names: z.string().min(2),
});

export type PostTopLevelRegionValues = z.infer<typeof PostTopLevelRegionSchema>;

export const PatchTopLevelRegionSchema = z.object({
  names: z.string().min(2).optional(),
  countries: z.array(z.object({ id: z.string().uuid(), names: z.string() })).optional(),
});

export type PatchTopLevelRegionValues = z.infer<typeof PatchTopLevelRegionSchema>;
